import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { endpoints, type Customer, type DocumentEnvelope } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/** `GET /customers/:id` → `{ data }`. */
export function useCustomer(id: string | undefined): UseQueryResult<Customer> {
  return useQuery({
    queryKey: queryKeys.customers.detail(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DocumentEnvelope<Customer>>(endpoints.customer(id ?? ""));
      return response.data;
    },
    enabled: id !== undefined && id !== "",
    staleTime: 60_000,
  });
}

/**
 * Create or update, whichever this screen is for.
 *
 * ONE INVALIDATION AFTER EITHER. A saved customer changes the list, the detail
 * and — because the invoice editor's picker reads the same list — the pickers
 * too. `queryKeys.customers.all` is the prefix every customer query shares, so
 * one call catches all of them; that is the whole reason the key factory exists.
 */
export function useSaveCustomer(id: string | undefined) {
  const queryClient = useQueryClient();
  const isEdit = id !== undefined && id !== "";

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const response = isEdit
        ? await api.put<DocumentEnvelope<Customer>>(endpoints.customer(id), body)
        : await api.post<DocumentEnvelope<Customer>>(endpoints.customers, body);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    },
  });
}

/** Transcribed from `describeGstin` in apps/api/src/services/gst-identity.ts. */
export interface GstinCheckResult {
  valid: boolean;
  /** Why not, when `valid` is false. Present ONLY on a failure. */
  reason?: string;
  /** The state the first two digits resolve to. Present only when valid. */
  stateCode?: string;
  stateName?: string;
  /** `INTRA_STATE` | `INTER_STATE`, against the company's own state (§6). */
  supplyType?: string;
}

/**
 * `POST /customers/validate-gstin` — is this GSTIN structurally valid, and which
 * state is it in?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS NOT A LOOKUP AGAINST THE GST PORTAL. It validates the STRUCTURE — 15
 * characters and the check digit — and resolves the state from the first two
 * digits, which is arithmetic on the string itself. It cannot tell you the trade
 * name, and it cannot tell you whether the registration is live. Nothing in this
 * app contacts a government API (§8.6 rules out the equivalent for HSN codes for
 * the same reason: an unverifiable answer on a legal document is worse than no
 * answer).
 *
 * IT DOES NOT CHECK FOR DUPLICATES either — the API answers 200 with
 * `valid: false` for a bad GSTIN, and a 409 from the SAVE is what reports "another
 * customer already has this". Two rows with one GSTIN means invoices raised
 * against the wrong one and the CA reconciling two ledgers for one buyer, so the
 * save is the right place to enforce it: it is the only point that can be atomic.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useValidateGstin() {
  return useMutation({
    mutationFn: async (gstin: string) =>
      api.post<GstinCheckResult>(endpoints.validateGstin, { gstin }),
  });
}
